/**
 * The local snapshot: everything the offline logger needs to be a logbook
 * rather than a notepad.
 *
 * ## The shape of the whole feature
 *
 * The offline page NEVER authenticates and never touches the network. It reads
 * this snapshot and writes sessions back. The authenticated app owns both ends:
 * it writes the snapshot while online, and it uploads finished sessions when a
 * connection returns. Neither side needs the other's abilities, which is the
 * only reason this is days of work instead of a local-first rewrite.
 *
 * ## Why the constants below are exported and duplicated
 *
 * `public/offline-log.html` is static: served by the service worker with no
 * bundle and no module system, so it cannot import a line of this file. It
 * opens the same database by name and reads the same stores in hand-written
 * vanilla JS. That duplication cannot be removed, only pinned - so every name
 * lives here as a constant and `offline-snapshot.test.ts` asserts the HTML
 * still spells each one the same way. The wordmark drifted for exactly this
 * reason (see brand.test.ts); this schema would be a worse thing to lose.
 *
 * ## Why a separate database from the write queue
 *
 * `active-workout-queue.ts` owns `outrepr-queue`. Sharing it would mean the
 * static page could trip that database's version upgrade, and a failed upgrade
 * there loses sets mid-workout. Different lifetimes, different risk, different
 * database.
 *
 * ## Full snapshot, not deltas
 *
 * `exercises` and `routines` carry `created_at` but no `updated_at`, so there
 * is no "what changed since" query to run without a migration. The payload is
 * a few kilobytes, so the whole thing is rewritten each time. Deltas are worth
 * building when the size actually hurts, and not before.
 */
import { newId } from "@/lib/uuid";

export const OFFLINE_DB_NAME = "outrepr-offline";
export const OFFLINE_DB_VERSION = 1;

/** Key-value bag: snapshotAt, deviceId. */
export const STORE_META = "meta";
/** Every exercise the lifter can log, keyed by id. */
export const STORE_EXERCISES = "exercises";
/** Their plans, each carrying its exercise list in order. */
export const STORE_PLANS = "plans";
/** Last session's sets per exercise - what makes this a logbook. */
export const STORE_LAST_SETS = "lastSets";
/** Sessions logged offline, waiting to be uploaded. */
export const STORE_SESSIONS = "sessions";

export const META_SNAPSHOT_AT = "snapshotAt";
export const META_DEVICE_ID = "deviceId";
/**
 * The lifter's display unit, carried so the offline page opens on the number
 * they expect.
 *
 * Storage stays kg everywhere - this is a render-layer preference, exactly as
 * it is in the live workout. Without it the offline logger had no way to know,
 * so an lb user walked into a basement gym and was shown kg: not wrong, but
 * not theirs, and the one screen with no way to change a setting.
 *
 * In meta rather than a store of its own because it is one scalar, and meta is
 * already the bag for "facts about this snapshot" - no schema bump needed to
 * add a key to a key-value store.
 */
export const META_WEIGHT_UNIT = "weightUnit";
/** Display unit for distances. Storage is always metres, exactly as weights
 *  are always kg - this only decides what the offline page prints. */
export const META_DISTANCE_UNIT = "distanceUnit";
/**
 * Rest-timer preferences, as three separate meta keys.
 *
 * They could have been one object under one key - they are read together and
 * written together - but the point of the test that pins these names is that
 * public/offline-log.html cannot import a line of this module, so the compiler
 * can never catch a rename. Three keys are three tripwires; one object key
 * would pin the key and leave the three field names inside it free to drift.
 *
 * WHY THEY ARE HERE AT ALL: the offline logger armed a hardcoded 120s rest
 * after every set. The app's actual chain is per-exercise rest_seconds -> the
 * user's default_rest_seconds -> DEFAULT_REST_SECONDS (60), so the offline
 * page was wrong even for a lifter who had never opened Settings, and it took
 * no notice of the two layers above that.
 */
export const META_DEFAULT_REST_SECONDS = "defaultRestSeconds";
export const META_REST_INCREMENT_SECONDS = "restIncrementSeconds";
export const META_URGENT_THRESHOLD_SECONDS = "urgentThresholdSeconds";
/** user_settings.count_warmups_in_volume. Defaults TRUE, i.e. warmups count.
 *  Carried because the offline logger can now tag a set as a warmup, and the
 *  SESSION chip beside it would otherwise disagree with the app for exactly
 *  the lifters who turned the setting off - the ones who care. */
export const META_COUNT_WARMUPS = "countWarmupsInVolume";
/**
 * Notes edited on the offline page and not yet on the server:
 * `Record<exercise_id, OfflineNoteEdit>` under one meta key.
 *
 * A meta key rather than a store of its own, because a new object store means
 * bumping OFFLINE_DB_VERSION - and the two openers of this database (the app,
 * and a static file with no bundle that may be running weeks-old code out of
 * the service worker cache) would then have to agree about the upgrade while
 * one of them is offline. The whole map is a few kilobytes at the cap.
 */
export const META_NOTE_EDITS = "noteEdits";
/**
 * The workout the APP currently has open, if any, so the offline logger can
 * see it.
 *
 * The two sides are otherwise blind to each other: the write queue lives in
 * its own database (outrepr-active-workout) and the snapshot never reads the
 * workouts table at all. Which means a lifter whose signal dies mid-session
 * can land on the offline page, start a session here, and end the night with
 * ONE gym visit split across TWO workouts - half the sets in each, a volume
 * that matches neither, and a level-up sweep that cannot fire because it needs
 * its sets in one session.
 *
 * Not cleared by the finish or discard paths. It is written by the workout
 * page and cleared by /log, which is only reachable when no workout is open
 * (that page redirects straight into a live one). So it self-heals on the next
 * visit either way, and never depends on catching an exit path.
 */
export const META_ACTIVE_WORKOUT = "activeWorkout";

/** The tags a lifter can put on a set, mirroring set-tag-picker's OPTIONS.
 *  NOT 'rescue': that one is offered by the live card when a set falls short
 *  and is locked once applied ("there is no way back"), so it is the app's to
 *  give, not a choice to hand someone in a basement gym. */
export const OFFLINE_SET_TAGS = [
  "normal",
  "warmup",
  "dropset",
  "failure",
  "stretch",
] as const;
export type OfflineSetTag = (typeof OFFLINE_SET_TAGS)[number];

/** The workout the app has open, as the offline logger needs to describe it. */
export type OfflineActiveWorkout = {
  id: string;
  /** The routine's name, or "Custom workout" - whatever the workout header is
   *  showing, so the two screens call the same session the same thing. */
  name: string;
  /** The workout's own started_at, NOT when this was stamped. Judged against
   *  the app's resume window, so a marker left behind by a force-quit stops
   *  claiming a session is live once that session would have gone stale. */
  startedAt: string;
};

/** One per-(user, exercise) note, edited offline and waiting to be written. */
export type OfflineNoteEdit = {
  /** The note as the lifter left it. The empty string is a real value: clearing
   *  a note is an edit, and user_exercise_notes keeps the row either way. */
  notes: string;
  /** DEVICE-clock ISO instant of the edit. The upload clamps it before using
   *  it, because this is the number that decides who wins when the same note
   *  also moved on the server while the phone was offline. */
  at: string;
};

export type SnapshotExercise = {
  id: string;
  name: string;
  muscle_group: string | null;
  tracks_weight: boolean;
  tracks_reps: boolean;
  /**
   * The cardio metrics, carried so the offline page can gate its cardio strip
   * the way the active card does.
   *
   * Without these the offline page had no way to tell a rowing machine from a
   * bench press: it knew muscle_group, so it could see "cardio", but not WHICH
   * of duration / distance / calories / heart rate that exercise records. A
   * stairmaster has no distance and a HIIT timer has only duration, so a card
   * that offered all four would be asking for numbers that do not exist.
   *
   * Defaulted to the CARDIO defaults for a cardio row and false otherwise when
   * the column is missing, so a snapshot written by an older build still reads.
   */
  tracks_duration: boolean;
  tracks_distance: boolean;
  tracks_calories: boolean;
  tracks_heart_rate: boolean;
  /**
   * The lifter's note for this exercise - pin position, seat height, grip.
   *
   * ONE note per (user, exercise), from user_exercise_notes: migration 0038
   * folded routine_exercises.notes into it precisely so a note follows the
   * exercise everywhere instead of being scoped to one routine. That is what
   * makes it worth carrying offline at all - "seat 3, pin 12" is a fact about
   * the machine, and the gym is where you need it.
   *
   * Empty string when there is no note. The offline page READS it and does not
   * write: the note is per-exercise rather than per-session, so an offline
   * edit would clobber whatever was written online since the snapshot, and a
   * snapshot can be days old.
   */
  notes: string;
  /** Rendered raw and uppercased by CSS, exactly as the active card does -
   *  there is no equipment label map in the app to duplicate. Null on the
   *  baseline seeds that were never tagged, and the card simply omits it. */
  equipment: string | null;
  /**
   * TRI-STATE, and all three states mean something different on screen:
   *   null    -> untagged. The card shows NO second line at all.
   *   []      -> explicitly no secondaries. The card reads "Isolation".
   *   [m,...] -> the secondary muscles, first four, joined with " · ".
   * Collapsing null and [] would make every untagged custom claim to be an
   * isolation exercise, which is a statement about the movement rather than
   * about the data.
   */
  secondary_muscle_groups: string[] | null;
};

export type SnapshotPlanExercise = {
  exercise_id: string;
  order_index: number;
  target_sets: number | null;
  target_reps: number | null;
  /** The most specific layer of the rest chain - routine_exercises.rest_seconds.
   *  Null means "not set", which falls through to the user's default. */
  rest_seconds: number | null;
  /**
   * routine_exercises.superset_group_id - members of one group share it.
   *
   * Carried so the offline page can SHOW that two exercises are paired and can
   * stop arming a rest mid-round. It cannot CREATE or BREAK a pairing: that
   * lives on routine_exercises, and the upload has no business writing to the
   * routine from a snapshot that may be days stale.
   */
  superset_group_id: string | null;
};

export type SnapshotPlan = {
  id: string;
  name: string;
  exercises: SnapshotPlanExercise[];
  /**
   * The schedule, copied straight off the routines row.
   *
   * These five are what turn the offline plan list from "whatever order
   * getAll happened to return" into the app's own answer to which session is
   * today's. The arithmetic that reads them - daysUntilNext for weekly,
   * anchor + length + positions for cycle - is pure: those columns plus the
   * device's clock, no server time, no history, no settings. So the offline
   * page can reach the same verdict the app reaches, rather than guessing or
   * showing nothing.
   */
  /**
   * routines.circuit_rounds. Non-null = this plan is a circuit.
   *
   * Carried so the offline page can SAY SO. It cannot run one: a circuit is
   * rounds of stations with its own timer and its own resume arithmetic, and
   * none of that exists on a static file. Without this column the page could
   * not even tell, so a five-station circuit rendered as five ordinary
   * exercise cards and the lifter logged one set each - a 5-set workout where
   * the plan called for 5 x N.
   *
   * Optional because a snapshot written before this was plumbed has no key,
   * and undefined has to read as "not a circuit" rather than as unknown.
   */
  circuit_rounds?: number | null;
  schedule_mode: string | null;
  scheduled_days: number[];
  cycle_length: number | null;
  /** Bare YYYY-MM-DD, parsed in the device's zone exactly as the app does. */
  cycle_anchor_date: string | null;
  cycle_days: number[];
  /** The lifter's own ordering, the last tie-break when two land on one day. */
  sort_order: number | null;
};

/**
 * The last set the lifter did on an exercise: weight, reps, when.
 *
 * ONE set, not a whole session, because that is what `user_last_sets` holds -
 * a distinct-on per exercise - and it is the same row the live card already
 * prefills from. Matching the app's own source means the offline page opens on
 * the number the online one would have shown, rather than a second opinion
 * assembled from a different query.
 */
export type SnapshotLastSets = {
  /** Keyed by exercise id. */
  exercise_id: string;
  weight: number | null;
  reps: number | null;
  performed_at: string | null;
};

export type OfflineSnapshot = {
  exercises: SnapshotExercise[];
  plans: SnapshotPlan[];
  lastSets: SnapshotLastSets[];
  /** Display unit only; every weight in here and in a logged session is kg. */
  weightUnit: "kg" | "lb";
  /** Display unit only; every distance in here and in a logged session is
   *  METRES, the same way weights are always kg. Carried for the same reason
   *  weightUnit is: the offline page has no way to ask. */
  distanceUnit: "km" | "mi";
  /** user_settings.default_rest_seconds, already resolved past its null. */
  defaultRestSeconds: number;
  /** user_settings.rest_increment_seconds - the +/- step. One of 10, 15, 30. */
  restIncrementSeconds: number;
  /** user_settings.urgent_threshold_seconds - the final-seconds flash window.
   *  0 disables the flash; the countdown still drains. */
  urgentThresholdSeconds: number;
  /** user_settings.count_warmups_in_volume. True = warmups count, the default. */
  countWarmupsInVolume: boolean;
};

/** One set logged offline. `id` is minted here so a replay is a no-op. */
export type OfflineSet = {
  /** A UUID in the SAME space the online path uses. addSet upserts on this id
   *  with ignoreDuplicates, so uploading twice cannot duplicate a set. */
  id: string;
  weight: number | null;
  reps: number | null;
  /**
   * One of OFFLINE_SET_TAGS, landing in sets.tag on upload.
   *
   * Optional on the type rather than required, because sessions logged before
   * tags existed are still sitting in IndexedDB waiting to upload and must not
   * become unreadable. Absent reads as 'normal', which is also the column's
   * own NOT NULL DEFAULT.
   */
  tag?: OfflineSetTag;
  /**
   * THE CARDIO SHAPE. All four optional, and absent is the ONLY safe default:
   * sessions logged before cardio existed offline are sitting in IndexedDB
   * right now waiting to upload, and they must keep reading. Same reasoning
   * the `tag` field above records.
   *
   * Canonical units, matching the columns and the online path exactly -
   * SECONDS, METRES, kcal, bpm - so the upload is a pass-through and no
   * conversion can go missing between two code paths. The page converts for
   * DISPLAY only, using snapshot.distanceUnit.
   *
   * A strength set carries none of these; a cardio set carries whatever its
   * exercise records and the lifter filled in. Both can be entirely absent:
   * "did some rowing, didn't time it" is a valid attendance log online and
   * stays one here.
   */
  durationSeconds?: number | null;
  distanceMeters?: number | null;
  caloriesKcal?: number | null;
  heartRateBpm?: number | null;
  /** Device-clock ISO instant. Carried to the server as the set's created_at,
   *  which addSet already accepts, so sets keep their real order. */
  at: string;
};

export type OfflineSessionEntry = {
  /** Copied off the plan at start, and written to workout_exercises on upload
   *  so a session logged offline arrives grouped the way it was performed. */
  superset_group_id?: string | null;
  /** Minted offline, and it becomes the workout_exercises row id on upload.
   *  All three levels - workout, exercise, set - carry a client id so the
   *  entire upload upserts do-nothing on a replay. Miss one and re-uploading
   *  a session duplicates everything below it. */
  id: string;
  exercise_id: string;
  /** Denormalised so a session still reads correctly if the exercise is
   *  renamed or deleted between logging and upload. */
  exercise_name: string;
  sets: OfflineSet[];
};

export type OfflineSession = {
  id: string;
  /** Device-clock ISO instants. These become the workout's started_at and
   *  ended_at, which is the whole point of backdating. */
  startedAt: string;
  endedAt: string | null;
  /** Minutes offset from UTC at the time of logging, from the DEVICE. Stored
   *  because an instant alone cannot say which local day a session belongs to:
   *  train at 8pm in Spain, sync in the UK, and the date moves. */
  tzOffsetMinutes: number;
  /** Which device logged it. The snapshot and its sessions are single-device
   *  by nature - there is no channel between phones while offline - so this
   *  exists to WARN on a mismatch, not to prevent one. */
  deviceId: string;
  /** The plan this session was started from, or null/"" for a free session.
   *  Written by the offline page at newSession() and read on upload so the
   *  workout lands attributed to its plan rather than as a one-off. Optional
   *  because a snapshot written before this was plumbed has no such key. */
  planId?: string | null;
  entries: OfflineSessionEntry[];
  /** Set once the authenticated app has uploaded it. */
  uploadedAt: string | null;
};

function hasIdb(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.indexedDB !== "undefined" &&
    window.indexedDB !== null
  );
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

/** Opens (and on first use creates) the offline database. */
export function openOfflineDb(): Promise<IDBDatabase | null> {
  if (!hasIdb()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = window.indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      // createObjectStore throws if the store exists, which it will on any
      // future version bump that adds one. Guarded per store rather than
      // per upgrade so adding a sixth cannot break the other five.
      for (const [name, keyPath] of [
        [STORE_META, "key"],
        [STORE_EXERCISES, "id"],
        [STORE_PLANS, "id"],
        [STORE_LAST_SETS, "exercise_id"],
        [STORE_SESSIONS, "id"],
      ] as const) {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath });
        }
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

function tx(
  db: IDBDatabase,
  stores: string[],
  mode: IDBTransactionMode,
): IDBTransaction {
  return db.transaction(stores, mode);
}

function done(t: IDBTransaction): Promise<boolean> {
  return new Promise((resolve) => {
    t.oncomplete = () => resolve(true);
    t.onerror = () => resolve(false);
    t.onabort = () => resolve(false);
  });
}

function allOf<T>(store: IDBObjectStore): Promise<T[]> {
  return new Promise((resolve) => {
    const req = store.getAll();
    req.onsuccess = () => resolve((req.result ?? []) as T[]);
    req.onerror = () => resolve([]);
  });
}

/**
 * Replace the whole snapshot. Exercises, plans and last-sets are cleared and
 * rewritten in ONE transaction, so a failure mid-write cannot leave a plan
 * pointing at an exercise that is no longer there.
 *
 * Sessions are untouched: they are the lifter's work, not cached server data,
 * and a refresh must never be able to eat them.
 */
export async function writeSnapshot(
  snapshot: OfflineSnapshot,
  deviceId: string,
): Promise<boolean> {
  const db = await openOfflineDb();
  if (!db) return false;
  try {
    const t = tx(
      db,
      [STORE_META, STORE_EXERCISES, STORE_PLANS, STORE_LAST_SETS],
      "readwrite",
    );
    const ex = t.objectStore(STORE_EXERCISES);
    const pl = t.objectStore(STORE_PLANS);
    const ls = t.objectStore(STORE_LAST_SETS);
    ex.clear();
    pl.clear();
    ls.clear();
    for (const e of snapshot.exercises) ex.put(e);
    for (const p of snapshot.plans) pl.put(p);
    for (const l of snapshot.lastSets) ls.put(l);
    const meta = t.objectStore(STORE_META);
    meta.put({ key: META_SNAPSHOT_AT, value: new Date().toISOString() });
    meta.put({ key: META_DEVICE_ID, value: deviceId });
    meta.put({ key: META_WEIGHT_UNIT, value: snapshot.weightUnit });
    meta.put({ key: META_DISTANCE_UNIT, value: snapshot.distanceUnit });
    meta.put({ key: META_DEFAULT_REST_SECONDS, value: snapshot.defaultRestSeconds });
    meta.put({ key: META_REST_INCREMENT_SECONDS, value: snapshot.restIncrementSeconds });
    meta.put({ key: META_URGENT_THRESHOLD_SECONDS, value: snapshot.urgentThresholdSeconds });
    meta.put({ key: META_COUNT_WARMUPS, value: snapshot.countWarmupsInVolume });
    return await done(t);
  } catch {
    return false;
  }
}

export type SnapshotStatus = {
  /**
   * FALSE when the read itself failed, and the counts below are then not
   * zero - they are UNKNOWN.
   *
   * Every field here used to come back as 0/null on a thrown transaction,
   * which is indistinguishable from a device that genuinely has nothing
   * stored. An error became a clean bill of health: the Settings pre-flight
   * told a lifter their plan was saved and nothing was waiting, and the
   * sign-out button - which warns only when pendingSessionCount > 0 - then
   * deleted real un-uploaded training with no confirm at all, because it had
   * been told there was none.
   *
   * Callers that ACT on a count (anything destructive) must treat false as
   * "assume there is something", not as zero.
   */
  ok: boolean;
  snapshotAt: string | null;
  deviceId: string | null;
  exerciseCount: number;
  planCount: number;
  pendingSessionCount: number;
};

/** What the Settings pre-flight check renders. */
export async function readSnapshotStatus(): Promise<SnapshotStatus> {
  const unknown: SnapshotStatus = {
    ok: false,
    snapshotAt: null,
    deviceId: null,
    exerciseCount: 0,
    planCount: 0,
    pendingSessionCount: 0,
  };
  const db = await openOfflineDb();
  // No IndexedDB at all is genuinely nothing stored, not a failed read: a
  // browser without the API cannot be holding a snapshot.
  if (!db) return { ...unknown, ok: true };
  try {
    const t = tx(
      db,
      [STORE_META, STORE_EXERCISES, STORE_PLANS, STORE_SESSIONS],
      "readonly",
    );
    const metaRows = await allOf<{ key: string; value: string }>(
      t.objectStore(STORE_META),
    );
    const exercises = await allOf<SnapshotExercise>(
      t.objectStore(STORE_EXERCISES),
    );
    const plans = await allOf<SnapshotPlan>(t.objectStore(STORE_PLANS));
    const sessions = await allOf<OfflineSession>(
      t.objectStore(STORE_SESSIONS),
    );
    const metaOf = (k: string) =>
      metaRows.find((r) => r.key === k)?.value ?? null;
    return {
      ok: true,
      snapshotAt: metaOf(META_SNAPSHOT_AT),
      deviceId: metaOf(META_DEVICE_ID),
      exerciseCount: exercises.length,
      planCount: plans.length,
      pendingSessionCount: sessions.filter((s) => s.uploadedAt == null).length,
    };
  } catch {
    return unknown;
  }
}

/** Sessions logged offline and not yet uploaded, oldest first. */
export async function readPendingSessions(): Promise<OfflineSession[]> {
  const db = await openOfflineDb();
  if (!db) return [];
  try {
    const t = tx(db, [STORE_SESSIONS], "readonly");
    const rows = await allOf<OfflineSession>(t.objectStore(STORE_SESSIONS));
    return rows
      .filter((s) => s.uploadedAt == null)
      .sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  } catch {
    return [];
  }
}

/**
 * Mark a session uploaded. Deliberately NOT a delete: a session is training
 * that cannot be re-created, so the local copy survives its own upload and the
 * lifter keeps a way to export it if anything downstream went wrong.
 */
export async function markSessionUploaded(sessionId: string): Promise<boolean> {
  const db = await openOfflineDb();
  if (!db) return false;
  try {
    const t = tx(db, [STORE_SESSIONS], "readwrite");
    const store = t.objectStore(STORE_SESSIONS);
    const existing = await new Promise<OfflineSession | null>((resolve) => {
      const r = store.get(sessionId);
      r.onsuccess = () => resolve((r.result as OfflineSession) ?? null);
      r.onerror = () => resolve(null);
    });
    if (!existing) return false;
    store.put({ ...existing, uploadedAt: new Date().toISOString() });
    return await done(t);
  } catch {
    return false;
  }
}

/**
 * Wipe every trace of the offline data from this device.
 *
 * Called on sign-out, and it has to be, because `public/offline-log.html` has
 * NO AUTHENTICATION. It cannot have any: it is a static file that runs with no
 * network, so there is nothing for it to check a session against. It reads
 * whatever is in this origin's IndexedDB and renders it.
 *
 * Which is fine while one person is signed in, and not fine the moment they
 * sign out: their plans, their exercise list and what they last lifted would
 * sit on the device indefinitely, readable by anyone who opens that URL. The
 * page's own strength - working with nothing - is exactly why the data has to
 * be removed rather than access-controlled.
 *
 * PENDING SESSIONS GO TOO, which is a real loss and the right call. Signing
 * out is a deliberate act, and leaving another person's training on the device
 * to be uploaded into the NEXT account that signs in would be worse. The
 * offline logger's Copy as text is the escape hatch, and the sign-out button
 * warns when anything is still waiting.
 */
export async function purgeOfflineData(): Promise<void> {
  const db = await openOfflineDb();
  if (!db) return;
  try {
    const t = tx(
      db,
      [STORE_META, STORE_EXERCISES, STORE_PLANS, STORE_LAST_SETS, STORE_SESSIONS],
      "readwrite",
    );
    for (const name of [
      STORE_META,
      STORE_EXERCISES,
      STORE_PLANS,
      STORE_LAST_SETS,
      STORE_SESSIONS,
    ]) {
      t.objectStore(name).clear();
    }
    await done(t);
  } catch {
    /* Best effort. A failure here must never block a sign-out. */
  }
}

/** Notes typed on the offline page and not yet on the server. */
export async function readNoteEdits(): Promise<Record<string, OfflineNoteEdit>> {
  const db = await openOfflineDb();
  if (!db) return {};
  try {
    const t = tx(db, [STORE_META], "readonly");
    const rows = await allOf<{ key: string; value: unknown }>(
      t.objectStore(STORE_META),
    );
    return coerceNoteEdits(rows.find((r) => r.key === META_NOTE_EDITS)?.value);
  } catch {
    return {};
  }
}

/** The meta value is whatever the static page last wrote, which is a file that
 *  can be older than this code. Anything not shaped like an edit is dropped
 *  rather than trusted into a server action. */
function coerceNoteEdits(raw: unknown): Record<string, OfflineNoteEdit> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, OfflineNoteEdit> = {};
  for (const [id, edit] of Object.entries(raw as Record<string, unknown>)) {
    if (!edit || typeof edit !== "object") continue;
    const { notes, at } = edit as { notes?: unknown; at?: unknown };
    if (typeof notes !== "string" || typeof at !== "string") continue;
    out[id] = { notes, at };
  }
  return out;
}

/**
 * Retire the edits the server has now dealt with.
 *
 * Two things, because doing either alone leaves a visibly wrong state:
 *
 *   - the pending edit is dropped, but ONLY if its `at` still matches what was
 *     uploaded. The lifter can type again between the read and the write, and
 *     that newer edit has to stay queued.
 *   - the cached exercise row takes the note that is now authoritative. Without
 *     it the offline page falls back to a snapshot that predates the edit, so a
 *     successful sync would read on that page as the note being UNDONE, until
 *     the next snapshot refresh - which may be days away.
 *
 * `notes` is what the server holds afterwards, which is not always what was
 * sent: an edit the server had already moved past settles with the value that
 * beat it.
 *
 * Reads run in their own transaction before the writes rather than interleaved
 * with them. An IndexedDB transaction stays alive across an await only while
 * each request is issued in the microtask the last one resolved in, and a loop
 * doing a get per settled note is a long way to walk that line for no gain.
 */
export async function settleNoteEdits(
  settled: Array<{ exercise_id: string; notes: string; at: string }>,
): Promise<void> {
  if (settled.length === 0) return;
  const db = await openOfflineDb();
  if (!db) return;
  try {
    const read = tx(db, [STORE_META, STORE_EXERCISES], "readonly");
    const metaRows = await allOf<{ key: string; value: unknown }>(
      read.objectStore(STORE_META),
    );
    const exRows = await allOf<SnapshotExercise>(
      read.objectStore(STORE_EXERCISES),
    );

    const pending = coerceNoteEdits(
      metaRows.find((r) => r.key === META_NOTE_EDITS)?.value,
    );
    const byId = new Map(exRows.map((e) => [e.id, e]));
    const rewrites: SnapshotExercise[] = [];
    for (const s of settled) {
      if (pending[s.exercise_id]?.at === s.at) delete pending[s.exercise_id];
      const row = byId.get(s.exercise_id);
      if (row && row.notes !== s.notes) rewrites.push({ ...row, notes: s.notes });
    }

    const write = tx(db, [STORE_META, STORE_EXERCISES], "readwrite");
    write.objectStore(STORE_META).put({ key: META_NOTE_EDITS, value: pending });
    const exStore = write.objectStore(STORE_EXERCISES);
    for (const row of rewrites) exStore.put(row);
    await done(write);
  } catch {
    /* Best effort. A failure here re-sends the same edit on the next pass,
     * which the upsert absorbs; losing the edit would not be absorbed by
     * anything. */
  }
}

/**
 * Stamp or clear the open-workout marker.
 *
 * Idempotent and best-effort by design: it runs from a render effect on two
 * hot pages, and a failure here must never be visible. The worst case of a
 * failed write is the offline logger not warning about a split session, which
 * is exactly where it was before this existed.
 */
export async function writeActiveWorkoutMarker(
  marker: OfflineActiveWorkout | null,
): Promise<void> {
  const db = await openOfflineDb();
  if (!db) return;
  try {
    const t = tx(db, [STORE_META], "readwrite");
    const store = t.objectStore(STORE_META);
    if (marker) store.put({ key: META_ACTIVE_WORKOUT, value: marker });
    else store.delete(META_ACTIVE_WORKOUT);
    await done(t);
  } catch {
    /* Best effort - see above. */
  }
}

const DEVICE_ID_KEY = "rl:deviceId";

/** A stable per-device id, minted on first use. Used to warn about a snapshot
 *  that belongs to another phone, which is the only honest thing to do about
 *  single-device state: there is no channel between devices while offline. */
export function getDeviceId(): string {
  if (typeof window === "undefined") return "server";
  try {
    const existing = window.localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const minted = newId();
    window.localStorage.setItem(DEVICE_ID_KEY, minted);
    return minted;
  } catch {
    // Private mode. A per-session id still lets the offline page stamp its
    // sessions; it just will not be stable across reloads.
    return newId();
  }
}
